import React, { FC, useEffect, useMemo, useState } from "react";
import { QuestionnaireType } from "@green0meter/api-client";
import { useTranslation } from "react-i18next";
import FeatherIcon from "feather-icons-react";
import dayjs from "dayjs";

import ShareDataModal from "./components/ShareDataModal";
import SessionExpiredModal from "./components/SessionExpiredModal";

import { Button, Tooltip } from "@components/base";
import {
  useGetCsobFormsMetadata,
  useGetCsobTokenExpiration,
  useGetCurrentCsobCompanyInfo,
} from "@api";
import { CsobRegimeType, useUserStore } from "@store/user";

const SendDataButton: FC = () => {
  const { t } = useTranslation();
  const { user, jwtTokenData } = useUserStore();
  const [fetchCsobTokenExpiration, setFetchCsobTokenExpiration] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [sessionExpiredModalOpen, setSessionExpiredModalOpen] = useState(false);
  const { availableFormsMetadata } = useGetCsobFormsMetadata(user);
  const { currentCsobCompanyInfo } = useGetCurrentCsobCompanyInfo(user);
  const {
    csobTokenExpiration,
    isErrorGetCsobTokenExpiration,
    isLoadingGetCsobTokenExpiration,
  } = useGetCsobTokenExpiration(user, fetchCsobTokenExpiration);

  const clientFormExists = useMemo(
    () =>
      availableFormsMetadata?.some(
        (form) => form.questionnaireType === QuestionnaireType.Client,
      ),
    [availableFormsMetadata],
  );

  const handleCloseShareModal = (): void => {
    setShareModalOpen(false);
    setFetchCsobTokenExpiration(false);
  };

  const handleCloseSessionExpiredModal = (): void => {
    setSessionExpiredModalOpen(false);
    setFetchCsobTokenExpiration(false);
  };

  const handleSendDataToCsobClick = (): void => {
    if (jwtTokenData?.csobRegimeType === CsobRegimeType.Open) {
      setShareModalOpen(true);
    } else {
      setFetchCsobTokenExpiration(true);
    }
  };

  useEffect(() => {
    if (fetchCsobTokenExpiration) {
      if (isErrorGetCsobTokenExpiration) {
        setSessionExpiredModalOpen(true);
      }
      if (csobTokenExpiration) {
        if (dayjs(csobTokenExpiration.expiresAt).isAfter(dayjs())) {
          setShareModalOpen(true);
        } else {
          setSessionExpiredModalOpen(true);
        }
      }
    }
  }, [csobTokenExpiration, isErrorGetCsobTokenExpiration, fetchCsobTokenExpiration]);

  return (
    <>
      <Tooltip
        arrow
        title={
          !currentCsobCompanyInfo?.isDataSendingEnabled
            ? t("csob.questionnaires.dataSendingDisabledTooltip")
            : !clientFormExists
              ? t("csob.questionnaires.csobButtonDisabledTooltip")
              : undefined
        }
        placement="top"
      >
        <Button
          size="small"
          disabled={
            !clientFormExists || !currentCsobCompanyInfo?.isDataSendingEnabled
          }
          startIcon={<FeatherIcon icon="send" />}
          onClick={handleSendDataToCsobClick}
          isLoading={isLoadingGetCsobTokenExpiration}
        >
          {t("csob.questionnaires.sendDataToCSOB")}
        </Button>
      </Tooltip>
      <ShareDataModal open={shareModalOpen} onClose={handleCloseShareModal} />
      <SessionExpiredModal
        open={sessionExpiredModalOpen}
        onClose={handleCloseSessionExpiredModal}
      />
    </>
  );
};

export default SendDataButton;
